/**
 * SeatSetupCard — "Teach us your seat" (register SF1). Four tap-a-seat picks, four quick
 * questions, then what the picks teach with every line editable. A skip path asks one
 * simple question. Per traveler when the owner has named travelers. Only the owner sees it.
 */
import { useEffect, useMemo, useState } from "react";
import { Armchair, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { seatPrefsApi } from "@/lib/api";
import { deriveFromPicks, orderLabel, pickMaps, pickOf, savedSummary, simplePrefs, type SeatPick } from "./seatPicks";
import SeatMapDraw, { SeatLegend } from "./SeatMapDraw";
import type { CompanionRule, PrefsSource, SeatPosition, SeatPreferencesResponse, SeatPrefs } from "./seatTypes";

type Phase =
  | { kind: "idle" }
  | { kind: "simple" }
  | { kind: "pick"; index: number }
  | { kind: "questions" }
  | { kind: "summary"; source: PrefsSource };

const selectClass =
  "h-11 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]";

const ORDERS: { value: string; order: SeatPosition[] }[] = [
  { value: "aisle,window,middle", order: ["aisle", "window", "middle"] },
  { value: "window,aisle,middle", order: ["window", "aisle", "middle"] },
  { value: "aisle,middle,window", order: ["aisle", "middle", "window"] },
  { value: "window,middle,aisle", order: ["window", "middle", "aisle"] },
  { value: "middle,aisle,window", order: ["middle", "aisle", "window"] },
];

function Choice<T extends string | number | boolean | null>({ label, value, options, onChange }: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-sm font-medium text-foreground">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={o.value === value}
            onClick={() => onChange(o.value)}
            className={
              "min-h-11 rounded-md border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] " +
              (o.value === value ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 font-medium text-foreground" : "border-input bg-background text-foreground hover:bg-muted")
            }
          >
            {o.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function Line({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[10rem_1fr] sm:items-center sm:gap-3">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

const nullableValue = (v: string | number | boolean | null) => (v === null ? "" : String(v));

export function SeatSummary({ prefs, onChange }: { prefs: SeatPrefs; onChange: (patch: Partial<SeatPrefs>) => void }) {
  return (
    <div className="space-y-3">
      <Line id="seat-order" label="Seat type">
        <select id="seat-order" className={selectClass} value={prefs.seatTypeOrder.join(",")} onChange={(e) => onChange({ seatTypeOrder: e.target.value ? (e.target.value.split(",") as SeatPosition[]) : [] })}>
          <option value="">No preference</option>
          {ORDERS.map((o) => <option key={o.value} value={o.value}>{orderLabel(o.order)}</option>)}
          {prefs.seatTypeOrder.length > 0 && !ORDERS.some((o) => o.value === prefs.seatTypeOrder.join(",")) && (
            <option value={prefs.seatTypeOrder.join(",")}>{orderLabel(prefs.seatTypeOrder)}</option>
          )}
        </select>
      </Line>
      <Line id="seat-exit" label="Exit row">
        <select id="seat-exit" className={selectClass} value={nullableValue(prefs.exitRow)} onChange={(e) => onChange({ exitRow: e.target.value === "" ? null : e.target.value === "true" })}>
          <option value="">No preference</option>
          <option value="true">Yes, first choice</option>
          <option value="false">Avoid</option>
        </select>
      </Line>
      <Line id="seat-bulkhead" label="Bulkhead">
        <select id="seat-bulkhead" className={selectClass} value={prefs.bulkhead ?? ""} onChange={(e) => onChange({ bulkhead: (e.target.value || null) as SeatPrefs["bulkhead"] })}>
          <option value="">No preference</option>
          <option value="want">Yes, I like the wall row</option>
          <option value="avoid">Avoid</option>
        </select>
      </Line>
      <Line id="seat-ahead" label="Where in the cabin">
        <select id="seat-ahead" className={selectClass} value={nullableValue(prefs.aheadOfRow)} onChange={(e) => onChange({ aheadOfRow: e.target.value === "" ? null : Number(e.target.value) })}>
          <option value="">Anywhere</option>
          {[...new Set([10, 15, 20, 25, 30, ...(prefs.aheadOfRow != null ? [prefs.aheadOfRow] : [])])].sort((a, b) => a - b).map((n) => (
            <option key={n} value={n}>Ahead of row {n}</option>
          ))}
        </select>
      </Line>
      <Line id="seat-toilets" label="Near the toilets">
        <select id="seat-toilets" className={selectClass} value={prefs.avoidToilets === true ? "avoid" : ""} onChange={(e) => onChange({ avoidToilets: e.target.value === "avoid" ? true : null })}>
          <option value="">Does not matter</option>
          <option value="avoid">Avoid</option>
        </select>
      </Line>
      <Line id="seat-side" label="Side of plane">
        <select id="seat-side" className={selectClass} value={prefs.side ?? ""} onChange={(e) => onChange({ side: (e.target.value || null) as SeatPrefs["side"] })}>
          <option value="">No preference</option>
          <option value="left">Left</option>
          <option value="right">Right</option>
        </select>
      </Line>
      <Line id="seat-companion" label="With a companion">
        <select id="seat-companion" className={selectClass} value={prefs.companionRule ?? ""} onChange={(e) => onChange({ companionRule: (e.target.value || null) as CompanionRule | null })}>
          <option value="">Not told</option>
          <option value="together">Side by side</option>
          <option value="across_aisle_ok">Across the aisle is fine</option>
          <option value="apart_ok">Does not matter</option>
        </select>
      </Line>
      <Line id="seat-worst" label="Worst you accept">
        <select id="seat-worst" className={selectClass} value={prefs.worstAccept ?? ""} onChange={(e) => onChange({ worstAccept: (e.target.value || null) as SeatPosition | null })}>
          <option value="">Anything</option>
          <option value="middle">A middle seat is fine</option>
          <option value="window">Window, not middle</option>
          <option value="aisle">Aisle only</option>
        </select>
      </Line>
      <Line id="seat-pay" label="Worth paying, each way">
        <select id="seat-pay" className={selectClass} value={prefs.payLimit === null ? "none" : String(prefs.payLimit)} onChange={(e) => onChange({ payLimit: e.target.value === "none" ? null : Number(e.target.value) })}>
          <option value="0">$0</option>
          <option value="50">Up to $50</option>
          <option value="100">Up to $100</option>
          <option value="none">No limit</option>
        </select>
      </Line>
      <Line id="seat-move" label="Worth moving a day">
        <select id="seat-move" className={selectClass} value={nullableValue(prefs.moveADay)} onChange={(e) => onChange({ moveADay: e.target.value === "" ? null : e.target.value === "true" })}>
          <option value="">Not told</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </Line>
    </div>
  );
}

export default function SeatSetupCard() {
  const [data, setData] = useState<SeatPreferencesResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [travelerKey, setTravelerKey] = useState("self");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [picks, setPicks] = useState<SeatPick[]>([]);
  const [draft, setDraft] = useState<SeatPrefs | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    seatPrefsApi.get().then(setData).catch(() => setLoadFailed(true));
  }, []);

  const maps = useMemo(() => pickMaps(data?.realFlight ?? null), [data?.realFlight]);
  const travelers = data?.travelers ?? [];
  const who = travelers.find((t) => t.key === travelerKey);
  const saved = data?.prefs.find((p) => p.travelerKey === travelerKey) ?? null;
  const headline = travelerKey === "self" || !who ? "Teach us your seat" : `Teach us ${who.name}'s seat`;

  const restart = () => {
    setPicks([]);
    setDraft(null);
    setSave("idle");
    setPhase({ kind: "idle" });
  };

  const choose = (index: number, pick: SeatPick) => {
    const next = [...picks.slice(0, index), pick];
    setPicks(next);
    if (index + 1 < maps.length) {
      setPhase({ kind: "pick", index: index + 1 });
    } else {
      setDraft(deriveFromPicks(next));
      setPhase({ kind: "questions" });
    }
  };

  const persist = async (source: PrefsSource) => {
    if (!draft) return;
    setSave("saving");
    try {
      const r = await seatPrefsApi.save({ travelerKey, ...draft, source, picks: source === "picks" ? picks : null });
      setData((d) => (d ? { ...d, prefs: r.prefs } : d));
      setSave("saved");
      setPhase({ kind: "idle" });
    } catch {
      setSave("error");
    }
  };

  const pickPhase = phase.kind === "pick" ? maps[phase.index] : null;
  const pickCabin = pickPhase?.layout.cabins[0] ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Armchair className="h-5 w-5" aria-hidden="true" />
          {headline}
        </CardTitle>
        <CardDescription>
          About a minute. Tap the seats you would take and we learn what you like. Only you can see this.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data && !loadFailed && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading" />}
        {loadFailed && <p className="text-sm text-muted-foreground">We could not load your seat answers. Try again in a moment.</p>}

        {data && travelers.length > 1 && phase.kind === "idle" && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Whose seat">
            {travelers.map((t) => (
              <button
                key={t.key}
                type="button"
                aria-pressed={t.key === travelerKey}
                onClick={() => { setTravelerKey(t.key); setSave("idle"); }}
                className={
                  "min-h-11 rounded-md border px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] " +
                  (t.key === travelerKey ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 font-medium" : "border-input hover:bg-muted")
                }
              >
                {t.name}
              </button>
            ))}
          </div>
        )}

        {data && phase.kind === "idle" && (
          <div className="space-y-3">
            {saved ? (
              <p className="text-sm text-foreground">{savedSummary(saved.prefs)}</p>
            ) : (
              <p className="text-sm text-muted-foreground">Nothing yet. Skip it and we ask one simple question.</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button type="button" className="min-h-11" onClick={() => { setPicks([]); setPhase({ kind: "pick", index: 0 }); }}>
                {saved ? "Do the picks again" : "Start the picks"}
              </Button>
              {saved && (
                <Button type="button" variant="outline" className="min-h-11" onClick={() => { setDraft(saved.prefs); setPicks([]); setPhase({ kind: "summary", source: "edited" }); }}>
                  Edit the answers
                </Button>
              )}
              <Button type="button" variant="ghost" className="min-h-11" onClick={() => setPhase({ kind: "simple" })}>
                Skip, ask one question
              </Button>
            </div>
            <p role="status" className="text-xs text-muted-foreground">
              {save === "saved" && "Saved. Your fares and seat watch use this now."}
            </p>
          </div>
        )}

        {phase.kind === "simple" && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">Aisle or window?</p>
            <div className="flex flex-wrap gap-2">
              {(["aisle", "window", "either"] as const).map((a) => (
                <Button
                  key={a}
                  type="button"
                  variant="outline"
                  className="min-h-11"
                  onClick={() => { setDraft(simplePrefs(a)); setPhase({ kind: "summary", source: "simple" }); }}
                >
                  {a === "aisle" ? "Aisle" : a === "window" ? "Window" : "Either, not a middle"}
                </Button>
              ))}
            </div>
            <Button type="button" variant="ghost" className="min-h-11" onClick={restart}>Cancel</Button>
          </div>
        )}

        {pickPhase && pickCabin && phase.kind === "pick" && (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Seat pick {phase.index + 1} of {maps.length}</p>
            <p className="text-sm font-medium text-foreground">{pickPhase.prompt}</p>
            <SeatMapDraw cabin={pickCabin} seats={pickPhase.seats} onPick={(s) => choose(phase.index, pickOf(pickPhase, s))} />
            <SeatLegend />
            <p className="text-xs text-muted-foreground">{pickPhase.note}</p>
            <div className="flex flex-wrap gap-2">
              {pickPhase.keepLooking && (
                <Button type="button" variant="outline" className="min-h-11" onClick={() => choose(phase.index, pickOf(pickPhase, null))}>
                  I would keep looking
                </Button>
              )}
              <Button type="button" variant="ghost" className="min-h-11" onClick={restart}>Cancel</Button>
            </div>
          </div>
        )}

        {phase.kind === "questions" && draft && (
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">A few quick questions</p>
            <Choice<CompanionRule | null>
              label="Flying with someone, how close is close enough?"
              value={draft.companionRule}
              onChange={(v) => setDraft({ ...draft, companionRule: v })}
              options={[
                { value: "together", label: "Side by side" },
                { value: "across_aisle_ok", label: "Across the aisle is fine" },
                { value: "apart_ok", label: "Does not matter" },
              ]}
            />
            <Choice<SeatPosition | null>
              label="What is the worst seat you would accept?"
              value={draft.worstAccept}
              onChange={(v) => setDraft({ ...draft, worstAccept: v })}
              options={[
                { value: "middle", label: "A middle seat is fine" },
                { value: "window", label: "Window, not middle" },
                { value: "aisle", label: "Aisle only" },
              ]}
            />
            <Choice<number | null>
              label="What would you pay for a better seat, each way?"
              value={draft.payLimit}
              onChange={(v) => setDraft({ ...draft, payLimit: v })}
              options={[
                { value: 0, label: "$0" },
                { value: 50, label: "Up to $50" },
                { value: 100, label: "Up to $100" },
                { value: null, label: "No limit" },
              ]}
            />
            <Choice<boolean | null>
              label="Would you move a day for a better seat?"
              value={draft.moveADay}
              onChange={(v) => setDraft({ ...draft, moveADay: v })}
              options={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
            />
            <Button type="button" className="min-h-11" onClick={() => setPhase({ kind: "summary", source: "picks" })}>See what we learned</Button>
          </div>
        )}

        {phase.kind === "summary" && draft && (
          <div className="space-y-4">
            <p className="text-sm font-medium text-foreground">What the picks teach us</p>
            <p className="text-xs text-muted-foreground">Change any line that is not right.</p>
            <SeatSummary
              prefs={draft}
              onChange={(patch) => {
                setDraft({ ...draft, ...patch });
                if (phase.source === "simple") setPhase({ kind: "summary", source: "edited" });
              }}
            />
            <p className="text-xs text-muted-foreground">
              Each traveler gets their own. We use several plane layouts, since a 737 and an A321 differ.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" className="min-h-11" disabled={save === "saving"} onClick={() => persist(phase.source)}>
                {save === "saving" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
                Save
              </Button>
              <Button type="button" variant="ghost" className="min-h-11" onClick={restart}>Cancel</Button>
              <span role="status" className="text-xs text-muted-foreground">
                {save === "error" && "That did not save. Try again."}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
